import { useAudio } from '@rhg/hooks/use-audio';
import playerMessages from '@rhg/lang/messages/player';
import {
    Box,
    Slider,
    Typography,
    useTheme,
    useMediaQuery,
    Theme,
} from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { displayTime } from '@rhg/utils/media';
import { useIntl } from 'react-intl';
import { AudioTimelineVariant } from '../types';

const styleTimingContainer = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};

const styleSmallTimeline = {
    mt: 4,
};

const styleTimingSlider = {
    width: '100%',
    mx: { xs: 0, sm: 0, md: 4 },
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};
const styleTimingSliderMinimal = { mx: 0 };
const styleTimingControl = {
    width: '100%',
    '& .MuiSlider-thumb': {
        width: 8,
        height: 8,
        color: (theme: Theme) =>
            theme.palette.mode === 'light' ? 'info.main' : 'info.light',
        '::before': {
            borderRadius: '50%',
            content: '""',
            width: 12,
            height: 12,
            backgroundColor: (theme: Theme) =>
                theme.palette.mode === 'light' ? 'info.main' : 'info.light',
            filter: 'opacity(20%)',
        },
    },
    '& .MuiSlider-thumb:not(.MuiSlider-active)': {
        transition: 'left 0.35s linear',
    },
    '& .MuiSlider-track': {
        transition: 'width 0.35s linear',
        color: (theme: Theme) =>
            theme.palette.mode === 'light' ? 'info.main' : 'info.light',
        height: 4,
    },
    '& .MuiSlider-rail': {
        color: (theme: Theme) =>
            theme.palette.mode === 'light' ? 'info.main' : 'info.light',
        height: 4,
    },
};

const styleTimingSliderControlMinimal = {
    py: 0,
    '& .MuiSlider-thumb': {
        width: 0,
        height: 0,
    },
};

export interface AudioTimelineProps {
    variant?: AudioTimelineVariant;
}
export function AudioTimeline({
    variant = AudioTimelineVariant.Large,
}: AudioTimelineProps) {
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const {
        controls: { timingState, setTime },
    } = useAudio();

    return (
        <>
            <Box
                sx={sxAppend(
                    styleTimingContainer,
                    variant === AudioTimelineVariant.Small && styleSmallTimeline
                )}
            >
                {variant !== AudioTimelineVariant.Minimal && !isMobile && (
                    <Typography
                        variant="subtitle2"
                        color="text.body"
                        component="div"
                        aria-label={formatMessage(
                            playerMessages['player.current_time'],
                            {
                                time: displayTime(timingState.currentTime),
                            }
                        )}
                    >
                        {displayTime(timingState.currentTime)}
                    </Typography>
                )}
                <Box
                    sx={sxAppend(
                        styleTimingSlider,
                        variant === AudioTimelineVariant.Minimal &&
                            styleTimingSliderMinimal
                    )}
                >
                    <Slider
                        sx={sxAppend(
                            styleTimingControl,
                            variant === AudioTimelineVariant.Minimal &&
                                styleTimingSliderControlMinimal
                        )}
                        value={timingState.currentTime}
                        max={timingState.duration}
                        aria-label={formatMessage(
                            playerMessages['player.seek_slider']
                        )}
                        getAriaValueText={(sliderValue) =>
                            formatMessage(
                                playerMessages['player.current_time'],
                                {
                                    time: displayTime(sliderValue),
                                }
                            )
                        }
                        step={timingState.duration / 1000}
                        size="medium"
                        onChangeCommitted={(_, newTime) => {
                            setTime(newTime as number);
                        }}
                    />
                </Box>
                {variant !== AudioTimelineVariant.Minimal && !isMobile && (
                    <Typography
                        variant="subtitle2"
                        color="text.body"
                        component="div"
                        aria-label={formatMessage(
                            playerMessages['player.duration'],
                            {
                                time: displayTime(timingState.duration),
                            }
                        )}
                    >
                        {displayTime(timingState.duration)}
                    </Typography>
                )}
            </Box>
            {variant !== AudioTimelineVariant.Minimal && isMobile && (
                <Box sx={styleTimingContainer}>
                    <Typography
                        variant="subtitle2"
                        color="text.body"
                        component="div"
                        aria-label={formatMessage(
                            playerMessages['player.current_time'],
                            {
                                time: displayTime(timingState.currentTime),
                            }
                        )}
                    >
                        {displayTime(timingState.currentTime)}
                    </Typography>
                    <Typography
                        variant="subtitle2"
                        color="text.body"
                        component="div"
                        aria-label={formatMessage(
                            playerMessages['player.duration'],
                            {
                                time: displayTime(timingState.duration),
                            }
                        )}
                    >
                        {displayTime(timingState.duration)}
                    </Typography>
                </Box>
            )}
        </>
    );
}
